import { GridRowId } from '@mui/x-data-grid-premium';
import styles from './ContractsTree.module.scss';
import { Button, LoadingDataGrid } from '@thought-river/ui-components';
import { Add, Gesture, Replay } from '@mui/icons-material';
import ContractUploadWizardModal from '../../components/ContractUploadWizardModal/ContractUploadWizardModal-container';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContractsDataGrid } from './components';
import {
    ContractTree,
    ContractTreeRow,
    ContractTreeUpdate,
    useGetContractsV10,
} from '@thought-river/negotiations-common/dist/api/contractManagement';
import ContractResource from '../../resources/ContractResource';
import {
    addTanStackQueryContractsRow,
    deleteTanStackQueryContractsRow,
    updateTanStackQueryContractsRow,
} from './helpers';
import { useQueryClient } from '@tanstack/react-query';
import { usePostSignContract } from '@thought-river/negotiations-common/dist/api/contractContent';
import { useAppSelector } from '@modules/common/hooks';
import BulkActions from './components/BulkActions';

const ContractsTree = () => {
    const queryClient = useQueryClient();

    const [contractUploadModalOpen, setContractUploadModalOpen] =
        useState(false);

    const { mutateAsync: signContract } = usePostSignContract({});

    const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);

    const {
        data: contractsData,
        isLoading: contractsLoading,
        refetch: fetchContractsData,
    } = useGetContractsV10<ContractTree>(
        {},
        {
            enabled: false,
            refetchOnWindowFocus: false,
            refetchInterval: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
        }
    );

    useEffect(() => {
        fetchContractsData();
    }, [fetchContractsData]);

    const pollingIndex = useRef<string | null>();
    const token = useAppSelector((state) => state.auth.apiToken);

    const abortController = useMemo(() => new AbortController(), []);

    const subscribe = useCallback(async () => {
        const headers = {
            Prefer: `wait=30${
                pollingIndex.current ? `;index=${pollingIndex.current}` : ''
            }`,
            Authorization: `Bearer ${token}`,
        };

        const response = await fetch(
            `${window.__APP_CONFIG__.api2Url}/contract-management/v1_0/contracts`,
            {
                headers,
                signal: abortController.signal,
            }
        );

        pollingIndex.current = response.headers.get('x-polling-index');

        if (response.status === 502) {
            await subscribe();
        } else if (response.status !== 200) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            await subscribe();
        } else {
            const message = (await response.json()) as ContractTreeUpdate;
            for (const deletedRowId of message.deleted) {
                deleteTanStackQueryContractsRow(queryClient, deletedRowId);
            }

            for (const newRow of message.new) {
                addTanStackQueryContractsRow(queryClient, newRow);
            }

            for (const updatedRow of message.updated) {
                updateTanStackQueryContractsRow(
                    queryClient,
                    updatedRow.id,
                    updatedRow
                );
            }

            await subscribe();
        }
    }, [abortController.signal, queryClient, token]);

    useEffect(() => {
        subscribe();

        return () => {
            abortController.abort();
        };
    }, [abortController, subscribe]);

    //Potentially won't need it once long polling contract status endpoints are done
    const handleContractUpdated = useCallback(
        (uuid: string, versionStatus: string, processingStatus: string) => {
            const existingRow: ContractTreeRow = contractsData?.rows.find(
                (r) => r.contract_uuid === uuid
            )!;

            updateTanStackQueryContractsRow(queryClient, existingRow.id, {
                version_status: versionStatus,
                processing_status: processingStatus,
            });

            setSelectedRows([]);
        },
        [contractsData?.rows, queryClient]
    );

    const handleReanalyzeContracts = useCallback(() => {
        const contractRows = contractsData?.rows.filter((row) =>
            selectedRows.includes(row.id)
        );
        contractRows?.forEach(
            ({ contract_uuid, deal_type, version_status }) => {
                ContractResource.reprocess(
                    [contract_uuid],
                    deal_type.name
                ).then(() => {
                    handleContractUpdated(
                        contract_uuid,
                        version_status,
                        'processing'
                    );
                });
            }
        );
    }, [contractsData?.rows, handleContractUpdated, selectedRows]);

    const handleSignContracts = useCallback(() => {
        const contractRows = contractsData?.rows.filter((row) =>
            selectedRows.includes(row.id)
        );

        contractRows?.forEach(
            ({ contract_uuid, version, processing_status }) => {
                signContract(
                    {
                        pathParams: {
                            contractUuid: contract_uuid,
                            contractVersion: version.number,
                        },
                    },
                    {
                        onSuccess: () => {
                            handleContractUpdated(
                                contract_uuid,
                                'Signed',
                                processing_status
                            );
                        },
                    }
                );
            }
        );
    }, [
        contractsData?.rows,
        handleContractUpdated,
        selectedRows,
        signContract,
    ]);

    const toolbarLeftContent = useMemo(
        () => (
            <div className={styles.actionsBar}>
                <Button
                    onClick={() => setContractUploadModalOpen(true)}
                    startIcon={<Add />}
                    data-testid="new-negotiation-btn"
                >
                    New negotiation
                </Button>
                <BulkActions
                    selectedCount={selectedRows.length}
                    actions={[
                        {
                            label: `Sign ${selectedRows.length} contracts`,
                            icon: <Gesture />,
                            onClick: handleSignContracts,
                        },
                        {
                            label: `Reanalyze ${selectedRows.length} contracts`,
                            icon: <Replay />,
                            onClick: handleReanalyzeContracts,
                        },
                    ]}
                />
            </div>
        ),
        [handleReanalyzeContracts, handleSignContracts, selectedRows.length]
    );

    return (
        <div className={styles.wrapper}>
            {contractsLoading ? (
                <LoadingDataGrid />
            ) : (
                <ContractsDataGrid
                    data={contractsData}
                    rowSelectionModel={selectedRows}
                    onSelectedRowsChange={setSelectedRows}
                    onContractUpdated={handleContractUpdated}
                    toolbarLeftContent={toolbarLeftContent}
                />
            )}
            <ContractUploadWizardModal
                open={contractUploadModalOpen}
                onClose={() => setContractUploadModalOpen(false)}
            />
        </div>
    );
};

export default ContractsTree;
